/** Page size for Agent chat history sidebar (recent + date windows). */
export const AGENT_CHAT_HISTORY_PAGE_SIZE = 20;

/**
 * @typedef {'recent' | '7d' | '30d'} AgentChatHistoryTimeRange
 */

/**
 * Build last_message_after / last_message_before ISO bounds for a time window.
 * Recent returns null (caller uses limit-only list).
 * @param {AgentChatHistoryTimeRange} timeRange
 * @param {Date} [now]
 * @returns {{ last_message_after: string|null, last_message_before: string|null } | null}
 */
export function agentChatHistoryDateBounds(timeRange, now = new Date()) {
  const ms = now.getTime();
  if (!Number.isFinite(ms)) return null;
  const iso = (d) => d.toISOString();
  if (timeRange === "7d") {
    return {
      last_message_after: iso(new Date(ms - 7 * 24 * 60 * 60 * 1000)),
      last_message_before: null,
    };
  }
  if (timeRange === "30d") {
    return {
      last_message_after: iso(new Date(ms - 30 * 24 * 60 * 60 * 1000)),
      last_message_before: null,
    };
  }
  return null;
}

/**
 * Narrow last_message_before to page deeper using the oldest row already shown.
 * @param {{ last_message_after: string|null, last_message_before: string|null }} bounds
 * @param {string|null|undefined} oldestLastMessageAt
 */
export function agentChatHistoryBoundsForOlderPage(bounds, oldestLastMessageAt) {
  const base = {
    last_message_after: bounds?.last_message_after || null,
    last_message_before: bounds?.last_message_before || null,
  };
  const cursor = String(oldestLastMessageAt || "").trim();
  if (!cursor) return base;
  if (!base.last_message_before || cursor < base.last_message_before) {
    base.last_message_before = cursor;
  }
  return base;
}

/**
 * @param {object} [opts]
 * @param {number} [opts.limit]
 * @param {number} [opts.offset]
 * @param {string|null} [opts.last_message_after]
 * @param {string|null} [opts.last_message_before]
 * @param {boolean} [opts.include_archived]
 */
export function buildAgentChatThreadsUrl({
  limit = AGENT_CHAT_HISTORY_PAGE_SIZE,
  offset = 0,
  last_message_after = null,
  last_message_before = null,
  include_archived = false,
} = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(Math.max(1, Math.min(100, limit))));
  params.set("offset", String(Math.max(0, offset)));
  if (last_message_after) params.set("last_message_after", last_message_after);
  if (last_message_before) params.set("last_message_before", last_message_before);
  if (include_archived) params.set("include_archived", "true");
  return `/api/agent/threads?${params.toString()}`;
}
